import React, { useState } from 'react';

import { Box, Button, RadioGroup, Text } from '@betterment/samba-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTrustAccountStep } from './TrustAccountFlow';

import StepHeading from '../../../AccountCreationFlow/components/StepHeading';
import { WorkflowLayout } from '../../../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { useSdiAccountStep } from '../SdiAccountFlow';

const NEW_TRUST_VALUE = 'new';

export const ExistingTrustSelection: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const [selectedTrustId, setSelectedTrustId] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { values: ownershipValues } = useSdiAccountStep('ownership');
  const { commit: commitSdi } = useSdiAccountStep('trust');
  const { commit: commitTrust } = useTrustAccountStep('existingTrustSelection');

  const trustAccounts = ownershipValues?.existingTrusts ?? [];

  const handleValueChange = (value: string) => {
    setSelectedTrustId(value);
    setErrorMessage(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedTrustId === undefined) {
      setErrorMessage('Please make a selection');
      return;
    }
    if (selectedTrustId === NEW_TRUST_VALUE) {
      void navigate(urlBuilder.appAddAccountDirectTradingTrustAccount.path);
      return;
    }
    commitSdi({ selectedTrustId });
    commitTrust({ selectedTrustId });
    void navigate(urlBuilder.appAddAccountDirectTradingInvestingExperience.path);
  };

  return (
    <WorkflowLayout
      pageTitle="Select existing trust"
      workflowTitle=""
      onBack={() => navigate(-1)}
      onClose={() => (returnTo ? navigate(returnTo) : navigate('/'))}
    >
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Box css={{ marginBottom: 'space.400' }}>
          <StepHeading>Would you like to add a goal to an existing trust?</StepHeading>
        </Box>

        <form onSubmit={handleSubmit}>
          <RadioGroup
            name="existing_trust_selection[trust_id]"
            appearance="default"
            onValueChange={handleValueChange}
            value={String(selectedTrustId)}
            data-testid="existing-trust-selection-radio-group"
            css={{ marginBottom: 'space.400' }}
          >
            <Box className="input-wrapper">
              {trustAccounts.map(trust => (
                <RadioGroup.ItemContainer key={trust.id}>
                  <Box
                    css={{
                      display: 'flex',
                      gap: 'space.150',
                      alignItems: 'center',
                      paddingY: 'space.150',
                      width: '100%',
                      md: { paddingLeft: 'space.300', paddingRight: '20px' },
                    }}
                  >
                    <RadioGroup.Item
                      id={`existing_trust_selection_${trust.id}`}
                      value={trust.id}
                      css={{
                        borderColor: '#363636',
                        width: 16,
                        height: 16,
                        '&:hover': { backgroundColor: 'background.surface !important' },
                        '&[data-state="checked"]': {
                          backgroundColor: 'background.surface',
                          borderWidth: 2,
                        },
                        '& > span:after': { backgroundColor: 'background.primary' },
                      }}
                      data-testid={`existing-trust-selection-${trust.id}-toggle`}
                    />
                    <RadioGroup.ItemLabel
                      htmlFor={`existing_trust_selection_${trust.id}`}
                      css={{ fontSize: '1.8rem' }}
                      data-testid={`existing-trust-selection-${trust.id}-label`}
                    >
                      {trust.displayName}
                    </RadioGroup.ItemLabel>
                  </Box>
                </RadioGroup.ItemContainer>
              ))}
              <RadioGroup.ItemContainer key={NEW_TRUST_VALUE}>
                <Box
                  css={{
                    display: 'flex',
                    gap: 'space.150',
                    alignItems: 'center',
                    paddingY: 'space.150',
                    width: '100%',
                    md: { paddingLeft: 'space.300', paddingRight: '20px' },
                  }}
                >
                  <RadioGroup.Item
                    id="existing_trust_selection_new"
                    value={NEW_TRUST_VALUE}
                    css={{
                      borderColor: '#363636',
                      width: 16,
                      height: 16,
                      '&:hover': { backgroundColor: 'background.surface !important' },
                      '&[data-state="checked"]': {
                        backgroundColor: 'background.surface',
                        borderWidth: 2,
                      },
                      '& > span:after': { backgroundColor: 'background.primary' },
                    }}
                    data-testid="existing-trust-selection-new-toggle"
                  />
                  <RadioGroup.ItemLabel
                    htmlFor="existing_trust_selection_new"
                    css={{ fontSize: '1.8rem' }}
                    data-testid="existing-trust-selection-new-label"
                  >
                    Create new trust
                  </RadioGroup.ItemLabel>
                </Box>
              </RadioGroup.ItemContainer>
            </Box>
          </RadioGroup>

          {errorMessage && (
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.100', marginBottom: 'space.400' }}>
              <Text variant="text100" color="text.critical">
                {errorMessage}
              </Text>
            </Box>
          )}

          <Button type="submit" variant="solidPrimary" css={{ width: 'fit-content' }}>
            Continue
          </Button>
        </form>
      </Box>
    </WorkflowLayout>
  );
};
